import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { fetchClientConfigLogic } from "./logic/fetchClientConfig";
import { connectClientLogic } from "./logic/connectClient";
import { disconnectClientLogic } from "./logic/disconnectClient";
import { fetchVersionLogic } from "./logic/fetchVersion";
import useApiErrorHandler from "../useApiErrorHandler";
import useAlerts from "../useAlerts";
import { printTicketLogic } from "./logic/printTicket";
import { verifyClientConnection } from "./logic/verifyClientConnection";
import { discoverPrintersLogic } from "./logic/discoverPrinters";
import { setPrinters, setSelectedPrinter } from "../../store/slices/qztray/qzTrayClientSlice";
import { useQzTrayPublicCertificate } from "./useQzTrayPublicCertificate";
import { useQzTrayClientSigner } from "./useQzTrayClientSigner";

export const useQzTrayClient = () => {
  const dispatch = useDispatch();
  const state = useSelector((s: RootState) => s.qzTrayClient);
  const payload = useSelector((s: RootState) => s.qzTrayPrint.payload);
  const hasFetched = useRef(false);

  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();

  const { certificate, fetchCertificate } = useQzTrayPublicCertificate();
  const { sign } = useQzTrayClientSigner();

  // ======================================================
  // 🔧 Cargar configuración desde backend
  // ======================================================
  const fetchClientConfig = useCallback(async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    await fetchClientConfigLogic(dispatch, handle, showAlert);
  }, [dispatch, handle, showAlert]);

  // ======================================================
  // 🔌 Conectar con QZ Tray
  // ======================================================
  // const connectClient = useCallback(async () => {
  //   await connectClientLogic(dispatch, state.config, handle, showAlert);
  // }, [dispatch, state.config, handle, showAlert]);
  // const connectClient = useCallback(async () => {
  //   await connectClientLogic(dispatch, state.config, handle, showAlert, sign);
  // }, [dispatch, state.config, handle, showAlert, sign]);

  const connectClient = useCallback(async () => {

    // 1) Si aún no tenemos certificado → obtenerlo
    const loadedCertificate = certificate || await fetchCertificate();

    // 2) Inyectar certificado en el config
    const configWithCertificate = {
      ...state.config,
      certificateContent: loadedCertificate,
    };

    // 3) Conectar usando firma digital
    await connectClientLogic(dispatch, configWithCertificate, handle, showAlert, sign);

  }, [dispatch, state.config, certificate, fetchCertificate, handle, showAlert, sign]);



  // ======================================================
  // 🧹 Desconectar cliente
  // ======================================================
  const disconnectClient = useCallback(async () => {
    await disconnectClientLogic(dispatch);
  }, [dispatch]);

  // ======================================================
  // 🔎 Consultar versión
  // ======================================================
  const fetchVersion = useCallback(async () => {
    await fetchVersionLogic(dispatch);
  }, [dispatch]);

  // ======================================================
  // 🧠 Verificar estado actual del cliente QZ
  // ======================================================
  const verifyConnection = useCallback(async () => {
    return await verifyClientConnection(dispatch, showAlert);
  }, [dispatch, showAlert]);

  // ======================================================
  // 🖨️ Método: impresión centralizada
  // ======================================================
  // const print = useCallback(
  //   async (printerName: string, commands: any[]) => {
  //     await printTicketLogic(dispatch, payload, printerName, commands, handle, showAlert);
  //   },
  //   [dispatch, state.config, handle, showAlert]
  // );

  const print = useCallback(
    async (printerName: string, commands: any[]) => {
      const loadedCertificate = certificate || await fetchCertificate();

      const configWithCertificate = {
        ...state.config,
        certificateContent: loadedCertificate,
      };

      await printTicketLogic(
        dispatch,
        payload,
        printerName,
        commands,
        handle,
        showAlert,
        configWithCertificate,
        sign
      );
    },
    [
      dispatch,
      payload,
      state.config,
      certificate,
      fetchCertificate,
      handle,
      showAlert,
      sign,
    ]
  );

  // ======================================================
  // 🖨️ Buscar impresoras disponibles
  // ======================================================
  const discoverPrinters = useCallback(async () => {

    // 1) Si no tenemos certificado aún → cargarlo
    const loadedCertificate = certificate || await fetchCertificate();
    // 2) Inyectamos certificateContent al config enviado a la lógica
    const configWithCertificate = {
      ...state.config,
      certificateContent: loadedCertificate,
    };

    return await discoverPrintersLogic(
      dispatch,
      configWithCertificate,
      handle,
      showAlert,
      sign,
      state.isConnected
    );

  }, [dispatch, state.config, state.isConnected, certificate, fetchCertificate, handle, showAlert, sign]);


  // 🖨️ Seleccionar impresora manualmente
  const setPrinter = useCallback(
    (name: string | null) => {
      dispatch(setSelectedPrinter(name));
    },
    [dispatch]
  );

  // 🖨️ Guardar lista detectada en estado global
  const savePrinters = useCallback(
    (list: string[]) => {
      dispatch(setPrinters(list));
    },
    [dispatch]
  );


  // ======================================================
  // 🔁 Retorno del hook
  // ======================================================
  return {
    ...state,
    fetchClientConfig,
    connectClient,
    disconnectClient,
    fetchVersion,
    verifyConnection,
    print,
    discoverPrinters,
    setPrinter,
    savePrinters
  };
};
